import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Sparkles, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Paywall({ onUnlock }) {
  const [email, setEmail] = useState("");
  const [checking, setChecking] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setChecking(true);
    try {
      const res = await axios.post(`${API}/premium/verify`, { email });
      if (res.data.premium) {
        localStorage.setItem("fb_sub_email", email.toLowerCase());
        toast.success("Premium unlocked — enjoy the full post!");
        onUnlock?.();
      } else if (res.data.subscribed) {
        toast.info("This email doesn't have premium access yet. Refer friends to earn it free!");
      } else {
        toast.error("This email isn't subscribed. Subscribe free first!");
      }
    } catch {
      toast.error("Could not verify. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="relative mt-0">
      {/* Gradient fade */}
      <div className="paywall-gradient absolute inset-0 -top-32 pointer-events-none z-10" />

      {/* Paywall box */}
      <div className="relative z-20 mx-auto max-w-md text-center px-6 py-10 bg-card border border-border rounded-xl shadow-sm">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent/10 mb-4">
          <Lock size={22} className="text-brand-accent" />
        </div>
        <h3 className="font-cormorant text-2xl font-semibold text-foreground mb-2">
          This post is for premium readers
        </h3>
        <p className="font-manrope text-sm text-muted-foreground leading-relaxed mb-6">
          Subscribe to unlock this post and every premium article — or earn premium free by
          referring friends.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
          <Link
            to="/subscribe"
            data-testid="paywall-subscribe-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-accent text-white text-sm font-medium font-manrope hover:bg-brand-accent-hover transition-colors duration-200"
          >
            <Sparkles size={15} />
            Subscribe to read
          </Link>
          <Link
            to="/referrals"
            data-testid="paywall-referral-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-border text-sm font-medium font-manrope text-foreground hover:bg-muted transition-colors duration-200"
          >
            <Gift size={15} />
            Earn it free
          </Link>
        </div>

        {/* Premium unlock */}
        <div className="border-t border-border pt-5">
          <p className="font-manrope text-xs text-muted-foreground mb-3">
            Already have premium access? Enter your email to unlock.
          </p>
          <form onSubmit={handleVerify} className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="paywall-unlock-email-input"
              className="flex-1 px-4 py-2.5 rounded-lg border border-border bg-background font-manrope text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
            />
            <button
              type="submit"
              disabled={checking}
              data-testid="paywall-unlock-btn"
              className="px-4 py-2.5 rounded-full border border-brand-accent text-brand-accent font-manrope text-sm font-medium hover:bg-accent/10 disabled:opacity-60 transition-colors"
            >
              {checking ? "Checking..." : "Unlock"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
